import React from "react";
import PropTypes from "prop-types";
import forEach from "lodash/forEach";

const missingOneTrustProvider =
  "You forgot to wrap your app in <OneTrustProvider>";
const OneTrustContext = React.createContext({
  get isInitialized() {
    throw new Error(missingOneTrustProvider);
  },
});
OneTrustContext.displayName = "OneTrustContext";

export const PERFORMANCE_SDKS = "C0002";
export const CONSENT_NOT_COLLECTED = -1;
export const CONSENT_NOT_GIVEN = 0;
export const CONSENT_GIVEN = 1;

const getConsentStatus = (categoryId) => {
  if (window.OnetrustActiveGroups.includes(categoryId)) {
    return CONSENT_GIVEN;
  }
  return CONSENT_NOT_GIVEN;
};

function useScript(src, attributes = []) {
  // Keep track of script status ("idle", "loading", "ready", "error")
  const [status, setStatus] = React.useState(src ? "loading" : "idle");
  React.useEffect(() => {
    // Allow falsy src value if waiting on other data needed for
    // constructing the script URL passed to this hook.
    if (!src) {
      setStatus("idle");
      return;
    }
    // Fetch existing script element by src
    // It may have been added by another intance of this hook
    let script = document.querySelector(`script[src="${src}"]`);
    if (!script) {
      // Create script
      script = document.createElement("script");
      script.src = src;
      script.async = true;
      script.fetchpriority = "low";
      script.setAttribute("data-status", "loading");
      forEach(attributes, (attribute) => {
        script.setAttribute(attribute.name, attribute.value);
      });

      // Add script to document body
      document.body.appendChild(script);
      // Store status in attribute on script
      // This can be read by other instances of this hook
      const setAttributeFromEvent = (event) => {
        script.setAttribute(
          "data-status",
          event.type === "load" ? "ready" : "error",
        );
      };
      script.addEventListener("load", setAttributeFromEvent);
      script.addEventListener("error", setAttributeFromEvent);
    } else {
      // Grab existing script status from attribute and set to state.
      setStatus(script.getAttribute("data-status"));
    }
    // Script event handler to update status in state
    // Note: Even if the script already exists we still need to add
    // event handlers to update the state for *this* hook instance.
    const setStateFromEvent = (event) => {
      setStatus(event.type === "load" ? "ready" : "error");
    };
    // Add event listeners
    script.addEventListener("load", setStateFromEvent);
    script.addEventListener("error", setStateFromEvent);
    // Remove event listeners on cleanup
    // eslint-disable-next-line consistent-return
    return () => {
      if (script) {
        script.removeEventListener("load", setStateFromEvent);
        script.removeEventListener("error", setStateFromEvent);
      }
    };
  }, [attributes, src]);
  return status;
}

export function Provider({ children, options }) {
  const [isInitialized, setIsInitialized] = React.useState(false);
  const { key } = options;
  const status = useScript(
    "https://cdn.cookielaw.org/scripttemplates/otSDKStub.js",
    [
      {
        name: "data-domain-script",
        value: key,
      },
    ],
  );

  /**
   * keep watching until OneTrust is ready.
   * This is necessary because even when the status is ready the script still fetches other scripts
   * and OneTrust will not be available until thoses scripts are available
   */
  React.useEffect(() => {
    let isCurrent = true;
    let intervalID = null;
    if (!isInitialized) {
      intervalID = setInterval(() => {
        if (isCurrent && status === "ready" && !!window.OneTrust) {
          setIsInitialized(true);
        }
      }, 100);
    }

    return () => {
      isCurrent = false;
      clearInterval(intervalID);
    };
  }, [isInitialized, status]);

  return (
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    <OneTrustContext.Provider value={{ isInitialized }}>
      {children}
    </OneTrustContext.Provider>
  );
}

export function useSDKStatus() {
  const { isInitialized } = React.useContext(OneTrustContext);
  return {
    isInitialized,
  };
}

export function useConsentStatus(categoryId) {
  const [consentStatus, setConsentStatus] = React.useState(
    CONSENT_NOT_COLLECTED,
  );
  const { isInitialized } = useSDKStatus();
  React.useEffect(() => {
    let isCurrent = true;
    if (isInitialized) {
      setConsentStatus(getConsentStatus(categoryId));

      window.OptanonWrapper = () => {
        if (isCurrent) {
          setConsentStatus(getConsentStatus(categoryId));
        }
      };
    }

    return () => {
      isCurrent = false;
    };
  }, [categoryId, isInitialized]);

  return consentStatus;
}

export function showPreferenceCenter() {
  if (window.OneTrust) {
    window.OneTrust.ToggleInfoDisplay();
  }
}

Provider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  options: PropTypes.shape({
    key: PropTypes.string.isRequired,
  }).isRequired,
};
